import React, { useState, useEffect, useRef, createContext, useContext, useMemo } from 'react';
import { Building2, ChevronDown, ChevronRight, Check, X, Users, Briefcase } from 'lucide-react';
import { apiFetch, readApiJson } from '../lib/api';
import { filtrerSocietes } from '../lib/rechercheSociete';
import {
  ENTITES_VIRTUELLES,
  PARTICULIER_ENTITY,
  PARTICULIER_ENTITY_ID,
  PROFESSIONNEL_ENTITY_ID,
  estVirtuelle,
  rafraichirEntite,
} from '../lib/entitesVirtuelles';

// Context for global entity filtering
const EntityContext = createContext(null);

// Sélecteur d'entités : « Toutes », les entités VIRTUELLES (« Particuliers (sans société) »,
// « Professionnels » — raccourcis de lecture sans fiche tk_clients derrière, définis UNE fois
// côté serveur, cf. lib/entitesVirtuelles.js) puis l'arbre des sociétés.
//
// Chaque écran TRADUIT l'entité choisie (lib/entitesVirtuelles : `paramsTickets` pour les
// tickets, `appartientAEntite` pour un filtre local, sentinelle traduite par le serveur
// ailleurs). Un écran où l'entité n'a pas de sens l'écarte EXPLICITEMENT : message « Choisissez
// une société » (Microsoft 365, rapports), bandeau « le filtre n'est pas disponible sur cet
// écran : affichage de toutes les entités » (sauvegardes, ESET, incidents, SNMP), ou bandeau qui
// dit ce que l'écran montre à la place (`EntityFilterBanner messageVirtuelle` : rapport resté sur
// la société de l'URL, organisation d'un acteur sans vue globale aux incidents).
// ⚠️ Une ancienne version promettait qu'« un écran non adapté affichera 0 » : c'était faux —
// plusieurs écrans affichaient la liste PRÉCÉDENTE, ou la 1ʳᵉ organisation. Ne jamais compter
// sur un 0 : tout nouvel écran qui lit l'entité doit dire ce qu'il fait d'une entité virtuelle.
//
// Exports gardés pour compatibilité : PARTICULIER_ENTITY_ID, PARTICULIER_ENTITY.
export { PARTICULIER_ENTITY_ID, PARTICULIER_ENTITY };

const ICONE_VIRTUELLE = { [PARTICULIER_ENTITY_ID]: Users, [PROFESSIONNEL_ENTITY_ID]: Briefcase };

/** Build a tree from flat list with parent_id. Returns {roots, childrenMap, allDescendantIds}. */
function buildTree(entities) {
  const childrenMap = {}; // parent_id → [entity, ...]
  const roots = [];
  entities.forEach(e => {
    if (e.parent_id) {
      if (!childrenMap[e.parent_id]) childrenMap[e.parent_id] = [];
      childrenMap[e.parent_id].push(e);
    } else {
      roots.push(e);
    }
  });
  // Pre-compute all descendant IDs for each entity (for include_children filtering)
  const descendantCache = {};
  function getDescendants(id) {
    if (descendantCache[id]) return descendantCache[id];
    const kids = childrenMap[id] || [];
    let all = kids.map(k => k.id);
    kids.forEach(k => { all = all.concat(getDescendants(k.id)); });
    descendantCache[id] = all;
    return all;
  }
  entities.forEach(e => getDescendants(e.id));
  return { roots, childrenMap, descendantCache };
}

export function EntityProvider({ children }) {
  const [selectedEntity, setSelectedEntity] = useState(() => {
    try {
      const saved = localStorage.getItem('selected_entity');
      // Entité virtuelle : nom et sous-titre RAFRAÎCHIS depuis la constante — un libellé
      // mémorisé avant un renommage ne doit pas survivre.
      return saved ? rafraichirEntite(JSON.parse(saved)) : null;
    } catch { return null; }
  });

  const [entities, setEntities] = useState([]);
  // État de l'arbre : `chargement` → `pret` | `echec`. Tant qu'il n'est pas `pret`, le champ
  // `interne` est inconnu : un filtre local « Professionnels » laisserait passer METERIS, MCF et
  // la SCI (relecture du 03/10/2026) — cf. lib/entitesVirtuelles.etatArbrePourEntite.
  const [etatArbre, setEtatArbre] = useState('chargement');
  // Identifiants des organisations internes (METERIS, MCF, SCI) servis par l'arbre MÊME quand le verrou cache leur
  // fiche : sans eux, un filtre local « Professionnels » comptait METERIS comme un client pour un non-superadmin
  // (relecture transverse du 30/10/2026). Jamais recopiés ici : lus dans la réponse du serveur.
  const [idsInternes, setIdsInternes] = useState([]);

  useEffect(() => {
    const recevoir = (res) => {
      setEntities(res.clients || []);
      setIdsInternes(Array.isArray(res.internes) ? res.internes : []);
      setEtatArbre('pret');
    };
    apiFetch('/api/tk/admin/clients/tree')
      .then(readApiJson)
      .then(recevoir)
      .catch(() => {
        // Fallback to old endpoint (il sert aussi `interne`)
        apiFetch('/api/tk/admin/clients')
          .then(readApiJson)
          .then(recevoir)
          .catch(() => setEtatArbre('echec'));
      });
  }, []);

  const tree = useMemo(() => buildTree(entities), [entities]);
  // Fiches de l'arbre par identifiant : porte `interne` (METERIS, MCF, SCI), lu par
  // lib/entitesVirtuelles.appartientAEntite pour les filtres locaux « Professionnels ».
  // Une organisation interne cachée par le verrou y figure quand même, réduite à `{ id, interne: true }` (aucune fiche).
  const societesParId = useMemo(() => {
    const parId = Object.fromEntries(entities.filter((e) => e && e.id).map((e) => [e.id, e]));
    idsInternes.forEach((id) => { if (id && !parId[id]) parId[id] = { id, interne: true }; });
    return parId;
  }, [entities, idsInternes]);

  const selectEntity = (entity) => {
    const choisie = rafraichirEntite(entity);
    setSelectedEntity(choisie);
    if (choisie) {
      localStorage.setItem('selected_entity', JSON.stringify(choisie));
    } else {
      localStorage.removeItem('selected_entity');
    }
  };

  // Get all IDs that should be included when filtering (entity + descendants)
  const selectedEntityIds = useMemo(() => {
    if (!selectedEntity) return null;
    if (estVirtuelle(selectedEntity)) return [selectedEntity.id];
    const descendants = tree.descendantCache[selectedEntity.id] || [];
    return [selectedEntity.id, ...descendants];
  }, [selectedEntity, tree]);

  return (
    <EntityContext.Provider value={{ selectedEntity, selectEntity, entities, tree, selectedEntityIds, societesParId, etatArbre }}>
      {children}
    </EntityContext.Provider>
  );
}

export function useEntity() {
  const context = useContext(EntityContext);
  if (!context) {
    throw new Error('useEntity must be used within EntityProvider');
  }
  return context;
}

/**
 * EntitySelector - Dropdown to select active entity for global filtering
 */
export default function EntitySelector() {
  const { selectedEntity, selectEntity, entities, tree } = useEntity();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  // Close on click outside or Escape
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Règle unique de lib/rechercheSociete (nom court, alias et préfixe servis par
  // /api/tk/admin/clients/tree ; accents et ponctuation ignorés).
  const filteredEntities = filtrerSocietes(entities, search);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {/* Selector Button */}
      <button
        ref={buttonRef}
        onClick={() => {
          if (!open && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setDropdownPos({ top: rect.bottom + 4, left: rect.left });
          }
          setOpen(!open);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 12px',
          background: selectedEntity ? 'rgba(39, 174, 96, 0.2)' : 'rgba(255,255,255,0.1)',
          border: '1px solid transparent',
          borderRadius: 6,
          color: '#fff',
          fontSize: 13,
          cursor: 'pointer',
          maxWidth: 200,
          transition: 'all 0.15s',
        }}
      >
        <Building2 size={16} style={{ flexShrink: 0 }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedEntity ? selectedEntity.name : 'Toutes entites'}
        </span>
        <ChevronDown size={14} style={{ flexShrink: 0, opacity: 0.7 }} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="dropdown-animate" style={{
          position: 'fixed',
          top: dropdownPos.top,
          left: dropdownPos.left,
          background: '#fff',
          borderRadius: 10,
          boxShadow: 'var(--shadow-xl)',
          border: '1px solid var(--border-light)',
          zIndex: 1000,
          width: 280,
          maxHeight: 400,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Search */}
          <div style={{ padding: 8, borderBottom: '1px solid #e8f0fe' }}>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher une entite..."
              style={{
                width: '100%',
                padding: '8px 10px',
                border: '1px solid #c0cfe0',
                borderRadius: 4,
                fontSize: 13,
                boxSizing: 'border-box',
              }}
              autoFocus
            />
          </div>

          {/* Options */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {/* All entities option */}
            <div
              onClick={() => { selectEntity(null); setOpen(false); setSearch(''); }}
              style={{
                ...optionStyle,
                fontWeight: !selectedEntity ? 600 : 400,
                background: !selectedEntity ? '#e8f0fe' : 'transparent',
              }}
            >
              <Building2 size={16} style={{ color: '#6b7d8f' }} />
              <span style={{ flex: 1 }}>Toutes les entites</span>
              {!selectedEntity && <Check size={16} style={{ color: '#27ae60' }} />}
            </div>

            {/* Entités virtuelles épinglées (aucune fiche derrière) — liste unique, lib/entitesVirtuelles */}
            {ENTITES_VIRTUELLES.map((virtuelle, i) => {
              const choisie = selectedEntity?.id === virtuelle.id;
              const Icone = ICONE_VIRTUELLE[virtuelle.id] || Users;
              return (
                <div
                  key={virtuelle.id}
                  data-testid={`entite-virtuelle-${virtuelle.id}`}
                  onClick={() => { selectEntity(virtuelle); setOpen(false); setSearch(''); }}
                  style={{
                    ...optionStyle,
                    fontWeight: choisie ? 600 : 400,
                    background: choisie ? '#e8f0fe' : 'transparent',
                    borderBottom: i === ENTITES_VIRTUELLES.length - 1 ? '2px solid #e8f0fe' : optionStyle.borderBottom,
                  }}
                >
                  <Icone size={16} style={{ color: choisie ? '#27ae60' : '#6b7d8f' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#1a3a5c', fontSize: 13 }}>{virtuelle.name}</div>
                    <div style={{ fontSize: 10, color: '#6b7d8f' }}>{virtuelle.sousTitre}</div>
                  </div>
                  {choisie && <Check size={16} style={{ color: '#27ae60' }} />}
                </div>
              );
            })}

            {/* Entity list — hierarchical if no search, flat if searching */}
            {search ? (
              <>
                {filteredEntities.map(entity => (
                  <EntityOption key={entity.id} entity={entity} depth={0}
                    isSelected={selectedEntity?.id === entity.id}
                    hasChildren={false}
                    onSelect={() => { selectEntity(entity); setOpen(false); setSearch(''); }} />
                ))}
                {filteredEntities.length === 0 && (
                  <div style={{ padding: 16, textAlign: 'center', color: '#6b7d8f', fontSize: 13 }}>
                    Aucune entite trouvee
                  </div>
                )}
              </>
            ) : (
              <EntityTree
                roots={tree?.roots || []}
                childrenMap={tree?.childrenMap || {}}
                selectedId={selectedEntity?.id}
                onSelect={(entity) => { selectEntity(entity); setOpen(false); }}
              />
            )}
          </div>

          {/* Clear button if entity selected */}
          {selectedEntity && (
            <div style={{ padding: 8, borderTop: '1px solid #e8f0fe' }}>
              <button
                onClick={() => { selectEntity(null); setOpen(false); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  width: '100%',
                  padding: '8px',
                  background: '#f4f7fb',
                  border: 'none',
                  borderRadius: 4,
                  color: '#6b7d8f',
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                <X size={14} /> Effacer le filtre
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const optionStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 12px',
  cursor: 'pointer',
  borderBottom: '1px solid #f4f7fb',
  transition: 'background 0.1s',
  fontSize: 13,
};

/* ── Hierarchical tree display ──────────────────────────────────────── */

function EntityTree({ roots, childrenMap, selectedId, onSelect }) {
  return (
    <>
      {roots.map(entity => (
        <EntityTreeNode key={entity.id} entity={entity} depth={0}
          childrenMap={childrenMap} selectedId={selectedId} onSelect={onSelect} />
      ))}
    </>
  );
}

function EntityTreeNode({ entity, depth, childrenMap, selectedId, onSelect }) {
  const children = childrenMap[entity.id] || [];
  const hasChildren = children.length > 0;
  const isSelected = selectedId === entity.id;
  const [expanded, setExpanded] = useState(isSelected || children.some(c => c.id === selectedId));

  return (
    <>
      <div
        style={{
          ...optionStyle,
          paddingLeft: 12 + depth * 20,
          fontWeight: isSelected ? 600 : 400,
          background: isSelected ? '#e8f0fe' : 'transparent',
        }}
      >
        {hasChildren ? (
          <span onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', width: 16 }}>
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </span>
        ) : (
          <span style={{ width: 16 }} />
        )}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}
          onClick={() => onSelect(entity)}>
          <Building2 size={14} style={{ color: isSelected ? '#27ae60' : '#6b7d8f', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ color: '#1a3a5c', fontSize: 13 }}>{entity.name}</div>
            {entity.city && <div style={{ fontSize: 10, color: '#6b7d8f' }}>{entity.city}</div>}
          </div>
          {hasChildren && <span style={{ fontSize: 10, color: '#9ca3af', marginRight: 4 }}>{children.length}</span>}
          {isSelected && <Check size={14} style={{ color: '#27ae60' }} />}
        </div>
      </div>
      {expanded && children.map(child => (
        <EntityTreeNode key={child.id} entity={child} depth={depth + 1}
          childrenMap={childrenMap} selectedId={selectedId} onSelect={onSelect} />
      ))}
    </>
  );
}

function EntityOption({ entity, isSelected, onSelect }) {
  return (
    <div onClick={onSelect} style={{
      ...optionStyle,
      fontWeight: isSelected ? 600 : 400,
      background: isSelected ? '#e8f0fe' : 'transparent',
    }}>
      <Building2 size={14} style={{ color: isSelected ? '#27ae60' : '#6b7d8f' }} />
      <div style={{ flex: 1 }}>
        <div style={{ color: '#1a3a5c' }}>{entity.name}</div>
        {entity.city && <div style={{ fontSize: 10, color: '#6b7d8f' }}>{entity.city}</div>}
      </div>
      {isSelected && <Check size={14} style={{ color: '#27ae60' }} />}
    </div>
  );
}
